import React from "react";
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LabelList, Legend,
} from "recharts";
import bead from "../data/panhandle-bead.json";

// Live replacements for the chart2/4/5 PNGs, drawn from NBO Final Proposal
// exhibits (see scripts/build-panhandle-bead.js). Colors match the PNG charts.
const TECH_COLOR = {
  "Fiber": "#2A7F62",
  "Licensed Fixed Wireless": "#C4942C",
  "LEO Satellite": "#B85C38",
};
const TECH_ORDER = ["LEO Satellite", "Licensed Fixed Wireless", "Fiber"];

const fmt$ = (v) => `$${Math.round(v).toLocaleString()}`;
const fmtN = (v) => Math.round(v).toLocaleString();
const pct = (part, whole) => `${Math.round((part / whole) * 100)}%`;

// ── Cost per location by provider ────────────────────────────────────────────
// providers: [{ name, tech, locs, funding }] with numeric locs/funding
export function ProviderCostChart({ providers }) {
  const data = providers
    .filter((p) => !p.tech.includes("CAI"))
    .map((p) => ({ ...p, avg: p.funding / p.locs }))
    .sort((a, b) => a.avg - b.avg);
  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data} layout="vertical" margin={{ top: 8, right: 110, left: 8, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E7E2D6" />
        <XAxis type="number" tickFormatter={(v) => `$${v / 1000}k`} tick={{ fontSize: 11 }} />
        <YAxis type="category" dataKey="short" width={118} tick={{ fontSize: 11 }} />
        <Tooltip formatter={(v, n, { payload }) => [`${fmt$(v)} (${fmtN(payload.locs)} locations)`, payload.tech]} />
        <Bar dataKey="avg" isAnimationActive={false}>
          {data.map((d) => <Cell key={d.name} fill={TECH_COLOR[d.tech] || "#999"} />)}
          <LabelList dataKey="avg" position="right"
            formatter={(v) => fmt$(v)} style={{ fontSize: 10, fill: "#444" }} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Panhandle share of statewide awards ──────────────────────────────────────
export function PanhandleShareChart() {
  const { statewide: s, panhandle: p } = bead;
  const rows = [
    { metric: "Locations", panhandle: p.locations, rest: s.locations - p.locations, fmt: fmtN },
    { metric: "BEAD support", panhandle: p.bead_support, rest: s.bead_support - p.bead_support, fmt: fmt$ },
    { metric: "Provider match", panhandle: p.match, rest: s.match - p.match, fmt: fmt$ },
  ].map((r) => {
    const total = r.panhandle + r.rest;
    return { ...r, panhandlePct: (r.panhandle / total) * 100, restPct: (r.rest / total) * 100 };
  });
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={rows} layout="vertical" stackOffset="expand" margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
        <XAxis type="number" tickFormatter={(v) => `${Math.round(v * 100)}%`} tick={{ fontSize: 11 }} />
        <YAxis type="category" dataKey="metric" width={96} tick={{ fontSize: 11 }} />
        <Tooltip formatter={(v, n, { payload }) => [
          `${payload.fmt(n === "Panhandle" ? payload.panhandle : payload.rest)} (${(n === "Panhandle" ? payload.panhandlePct : payload.restPct).toFixed(1)}%)`, n,
        ]} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar dataKey="panhandlePct" name="Panhandle" stackId="a" fill="#A84F30" isAnimationActive={false}>
          <LabelList dataKey="panhandlePct" position="insideLeft"
            formatter={(v) => `${v.toFixed(1)}%`} style={{ fontSize: 11, fill: "#fff", fontWeight: 700 }} />
        </Bar>
        <Bar dataKey="restPct" name="Rest of Nebraska" stackId="a" fill="#C9CFC9" isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Technology mix: Panhandle vs statewide (share of locations) ──────────────
function TechPie({ title, counts }) {
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  const data = TECH_ORDER.map((t) => ({ name: t, value: counts[t] || 0 }));
  return (
    <div style={{ flex: "1 1 160px", minWidth: 160 }}>
      <div style={{ textAlign: "center", fontSize: 12, fontWeight: 700, color: "#122326" }}>{title}</div>
      <div style={{ textAlign: "center", fontSize: 11, color: "#767676" }}>{fmtN(total)} locations</div>
      <ResponsiveContainer width="100%" height={190}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" outerRadius={70} isAnimationActive={false}
            label={({ value }) => (value ? pct(value, total) : "")} labelLine={false} fontSize={11}>
            {data.map((d) => <Cell key={d.name} fill={TECH_COLOR[d.name]} />)}
          </Pie>
          <Tooltip formatter={(v, n) => [`${fmtN(v)} (${pct(v, total)})`, n]} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

export function PanhandleTechChart() {
  const panhandle = Object.fromEntries(
    Object.entries(bead.byTechnology).map(([t, g]) => [t, g.locations])
  );
  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        <TechPie title="Panhandle" counts={panhandle} />
        <TechPie title="Statewide" counts={bead.statewide.locationsByTechnology} />
      </div>
      <div style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 14, fontSize: 11, color: "#444" }}>
        {TECH_ORDER.map((t) => (
          <span key={t} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 10, height: 10, background: TECH_COLOR[t], display: "inline-block", borderRadius: 2 }} />
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}
